import React from "react";
import {
    VStack,
    Heading,
    Text,
    Box,
    Container,
    Icon,
    Flex,
    Divider,
    Link,
    useColorModeValue,
    UnorderedList,
    ListItem,
    Button,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { FaUserTimes, FaArrowLeft, FaEnvelope, FaClock } from "react-icons/fa";

const Section = ({ icon, title, children, accentColor, cardBg, textColor, borderColor }) => (
    <Box
        w="100%"
        bg={cardBg}
        border="1px solid"
        borderColor={borderColor}
        rounded="xl"
        p={6}
        shadow="sm"
        transition="all 0.2s"
        _hover={{ shadow: "md" }}
    >
        <Flex align="center" mb={4} gap={3}>
            <Flex
                align="center"
                justify="center"
                bg={useColorModeValue("brand.50", "brand.900")}
                p={2}
                rounded="lg"
                color={accentColor}
                flexShrink={0}
            >
                <Icon as={icon} boxSize={5} />
            </Flex>
            <Heading fontSize={{ base: "md", md: "lg" }} fontWeight="700" color={textColor}>
                {title}
            </Heading>
        </Flex>
        <Box color={useColorModeValue("gray.600", "gray.400")} fontSize="md" lineHeight="tall">
            {children}
        </Box>
    </Box>
);

const DeleteAccount = () => {
    const navigate = useNavigate();
    const bg = useColorModeValue("gray.50", "gray.900");
    const cardBg = useColorModeValue("white", "gray.800");
    const textColor = useColorModeValue("gray.800", "white");
    const subTextColor = useColorModeValue("gray.600", "gray.400");
    const accentColor = useColorModeValue("brand.500", "brand.200");
    const borderColor = useColorModeValue("gray.200", "gray.700");

    const sectionProps = { accentColor, cardBg, textColor, borderColor };

    return (
        <>
            <Helmet>
                <title>Delete Account | Techy India</title>
                <meta
                    name="description"
                    content="Request deletion of your Techyindia account and associated data."
                />
            </Helmet>

            <VStack
                w="100%"
                minH="100vh"
                bg={bg}
                spacing={0}
                align="stretch"
                py={8}
            >
                <Container maxW="container.lg">
                    <VStack spacing={8} align="center" w="100%">

                        {/* Back Button */}
                        <Flex w="100%" justify="flex-start">
                            <Button
                                leftIcon={<Icon as={FaArrowLeft} />}
                                onClick={() => navigate(-1)}
                                variant="ghost"
                                size="sm"
                                color={accentColor}
                                _hover={{ bg: useColorModeValue("brand.50", "brand.900") }}
                            >
                                Go Back
                            </Button>
                        </Flex>

                        {/* Header */}
                        <VStack spacing={4} textAlign="center">
                            <Flex
                                align="center"
                                justify="center"
                                bg={useColorModeValue("brand.100", "brand.900")}
                                p={4}
                                rounded="full"
                                color="brand.500"
                            >
                                <Icon as={FaUserTimes} boxSize={7} />
                            </Flex>
                            <Heading
                                fontSize={{ base: "2xl", md: "4xl" }}
                                fontWeight="800"
                                color={textColor}
                                letterSpacing="-0.5px"
                            >
                                Delete{" "}
                                <Text as="span" color="brand.500">
                                    Account
                                </Text>
                            </Heading>
                            <Text color={subTextColor} fontSize="md" maxW="600px">
                                Learn how to request the deletion of your account and associated data.
                            </Text>
                        </VStack>

                        <Divider borderColor={borderColor} />

                        {/* Delete Account Request Info */}
                        <Section icon={FaUserTimes} title="Delete Account Request" {...sectionProps}>
                            <Text mb={4}>
                                Users can request deletion of their Techyindia account and associated data by contacting us directly.
                            </Text>

                            <Text fontWeight="600" color={textColor} mb={2}>
                                Contact Email:
                            </Text>
                            <Flex align="center" gap={2} mb={4}>
                                <Icon as={FaEnvelope} color={accentColor} />
                                <Link
                                    href="mailto:support@techyindia.app"
                                    color={accentColor}
                                    fontWeight="600"
                                    _hover={{ textDecoration: "underline" }}
                                >
                                    support@techyindia.app
                                </Link>
                            </Flex>

                            <Text fontWeight="600" color={textColor} mb={2}>
                                Please include the following in your email:
                            </Text>
                            <UnorderedList spacing={2} pl={2} mb={4}>
                                <ListItem>Registered email address</ListItem>
                                <ListItem>Reason for deletion request</ListItem>
                            </UnorderedList>

                            <Flex align="center" gap={2} mt={6} p={4} bg={useColorModeValue("gray.50", "gray.700")} rounded="lg">
                                <Icon as={FaClock} color="gray.500" />
                                <Text fontSize="sm" color={subTextColor} fontWeight="500">
                                    Requests are reviewed and processed within 7 business days.
                                </Text>
                            </Flex>
                        </Section>

                    </VStack>
                </Container>
            </VStack>
        </>
    );
};

export default DeleteAccount;
